import { useState } from 'react';
import { useNotifications } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { GENERATE_TEXT_TO_SPEECH, TEXT_TO_SPEECH_AUDIO_DATA } from 'constants';

// \p{L} matches any Unicode character classified as a letter
const regexUnicode = /\p{L}/u;

/**
 * Custom hook for handling text-to-speech audio generation and playback
 *
 * @param {string} textContainerId - Unique identifier for the text container to associate audio with
 * @returns {Object} Audio player state and control functions
 * @returns {string|null} returns.audioSrc - Current audio source URL
 * @returns {Function} returns.handlePlayAudio - Function to generate and play audio
 * @returns {Function} returns.handleStopAudio - Function to stop playback and clear audio
 * @returns {Function} returns.checkIsCorrectText - Function to validate text for TTS conversion
 * @returns {boolean} returns.isLoadingAudio - Flag indicating if audio is currently being generated
 * @returns {boolean} returns.isAudioLoaded - Flag indicating if audio is loaded and associated with current container
 */
const useTextToSpeech = (textContainerId) => {
  const dispatch = useDispatch();
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const { showNotification } = useNotifications();
  const { audioSrc, textOwnerId } = useSelector((state) => state.textToSpeech);
  const elevenlabs = useSelector((state) => state.config.elevenlabs);
  const { apiKey } = elevenlabs ?? {};

  /**
   * Validates that the text contains at least one letter character
   *
   * @param {string} text - Text to validate for TTS conversion
   * @returns {boolean} True if the text is non-empty and contains at least one letter character
   */
  const checkIsCorrectText = (text) => {
    const trimmed = text?.trim();
    return Boolean(trimmed && regexUnicode.test(trimmed));
  };

  /**
   * Updates the audio source and properly revokes previous object URL to prevent memory leaks
   *
   * @param {string|null} newSrc - The new audio source URL to set, or null to clear
   * @param {string|null} textOwnerId - Identifier of the text container owning this audio
   */
  const updateAudioSrc = (newSrc, textOwnerId = null) => {
    if (audioSrc) URL.revokeObjectURL(audioSrc);
    dispatch({
      type: TEXT_TO_SPEECH_AUDIO_DATA,
      payload: { audioSrc: newSrc, textOwnerId },
    });
  };

  /**
   * Generates and plays audio from the provided text
   *
   * @param {string} text - The text to convert to speech
   * @param {string} textOwnerId - Identifier of the text container requesting audio generation
   * @returns {void}
   */
  const handlePlayAudio = (text, textOwnerId) => {
    if (isLoadingAudio || !checkIsCorrectText(text)) return; // Prevent multiple or empty requests

    setIsLoadingAudio(true);
    updateAudioSrc(null, null); // Clear previous audio

    dispatch({
      type: GENERATE_TEXT_TO_SPEECH.REQUEST,
      payload: {
        text,
        callback: ({ audioUrl, error = '', cancelled = false }) => {
          setIsLoadingAudio(false);
          if (cancelled || error) {
            updateAudioSrc(null, null);
            if (error) showNotification('error', error);
          } else if (audioUrl) {
            updateAudioSrc(audioUrl, textOwnerId);
          }
        },
      },
    });
  };

  /**
   * Stops audio playback and clears the current audio source
   *
   * @returns {void}
   */
  const handleStopAudio = () => {
    updateAudioSrc(null, null);
  };

  // Determine if valid audio is loaded for the current text container
  const isAudioLoaded = audioSrc && textOwnerId === textContainerId;

  // Indicates if the user has an API key configured for the text-to-speech service
  const textToSpeechAvailable = Boolean(apiKey);

  return {
    audioSrc,
    handlePlayAudio,
    handleStopAudio,
    checkIsCorrectText,
    isLoadingAudio,
    isAudioLoaded,
    textToSpeechAvailable,
  };
};

export default useTextToSpeech;
